// @flow

import React, { Component } from 'react';

/**
 * The type of the React {@code Component} props of {@link ThumbnailVideoBackground}.
 */
type Props = {

    /**
     * Additional CSS class name.
     */
    className?: string,

    /**
     * Whether this is a local participant thumbnail.
     */
    isLocal?: boolean,

    /**
     * Whether the video background should display flipped horizontally.
     */
    mirror?: boolean,

    /**
     * ID of the participant.
     */
    participantId?: string,

    /**
     * Direct reference to the video element if available.
     */
    videoElement?: ?HTMLVideoElement,

    /**
     * Video track associated with this thumbnail.
     */
    videoTrack?: any
};

/**
 * The type of the React {@code Component} state of {@link ThumbnailVideoBackground}.
 */
type State = {

    /**
     * Indicates whether at least one frame has been successfully drawn.
     */
    hasFrame: boolean
};

/**
 * Implements a React Component which displays a blurred video canvas
 * behind a thumbnail to fill empty space when object-fit is set to contain.
 *
 * @augments Component
 */
class ThumbnailVideoBackground extends Component<Props, State> {
    _canvasEl: ?HTMLCanvasElement;
    _videoElement: ?HTMLVideoElement;
    _updateCanvasInterval: ?IntervalID;

    /**
     * Initializes a new {@code ThumbnailVideoBackground} instance.
     *
     * @param {Props} props - The read-only properties with which the new instance
     * is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        this.state = {
            hasFrame: false
        };

        this._canvasEl = null;
        this._videoElement = null;
        this._updateCanvasInterval = null;

        this._setCanvasEl = this._setCanvasEl.bind(this);
        this._updateCanvas = this._updateCanvas.bind(this);
    }

    /**
     * Sets the interval to draw video frames onto the canvas.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidMount() {
        this._startInterval();
    }

    /**
     * Cleans up canvas state when participant or video element changes.
     *
     * @inheritdoc
     */
    componentDidUpdate(prevProps: Props) {
        if (this.props.videoTrack?.muted || !this.props.videoTrack) {
            this._stopInterval();
            this._videoElement = null;
            this._clearCanvas();
            if (this.state.hasFrame) {
                this.setState({ hasFrame: false });
            }

            return;
        }

        if (prevProps.participantId !== this.props.participantId
            || prevProps.videoElement !== this.props.videoElement
            || prevProps.videoTrack !== this.props.videoTrack) {
            this._videoElement = null;
            this._clearCanvas();
            if (this.state.hasFrame) {
                this.setState({ hasFrame: false });
            }
        }

        if (!this._updateCanvasInterval && this.props.videoTrack && !this.props.videoTrack.muted) {
            this._startInterval();
        }
    }

    /**
     * Clears the update interval and canvas on unmount.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentWillUnmount() {
        this._stopInterval();
        this._clearCanvas();
    }

    /**
     * Starts interval to periodically sample the video element.
     *
     * @private
     * @returns {void}
     */
    _startInterval() {
        this._stopInterval();

        if (this.props.videoTrack?.muted || !this.props.videoTrack) {
            return;
        }

        this._updateCanvas();
        this._updateCanvasInterval = setInterval(this._updateCanvas, 1000);
    }

    /**
     * Clears the update interval.
     *
     * @private
     * @returns {void}
     */
    _stopInterval() {
        if (this._updateCanvasInterval) {
            clearInterval(this._updateCanvasInterval);
            this._updateCanvasInterval = null;
        }
    }

    /**
     * Clears any frame drawn onto the canvas.
     *
     * @private
     * @returns {void}
     */
    _clearCanvas() {
        if (this._canvasEl) {
            const ctx = this._canvasEl.getContext('2d');

            if (ctx) {
                ctx.clearRect(0, 0, this._canvasEl.width, this._canvasEl.height);
            }
        }
    }

    _setCanvasEl: (?HTMLCanvasElement) => void;

    /**
     * Sets the ref for the canvas DOM element.
     *
     * @param {?HTMLCanvasElement} el - The canvas DOM element.
     * @private
     * @returns {void}
     */
    _setCanvasEl(el: ?HTMLCanvasElement) {
        this._canvasEl = el;
    }

    /**
     * Finds the corresponding video element for this thumbnail.
     *
     * @private
     * @returns {?HTMLVideoElement}
     */
    _getVideoElement(): ?HTMLVideoElement {
        if (this.props.videoElement) {
            return this.props.videoElement;
        }

        if (this.props.videoTrack?.mhTrack?.containers?.length) {
            for (const c of this.props.videoTrack.mhTrack.containers) {
                const video = c.querySelector ? c.querySelector('video') : c.tagName === 'VIDEO' ? c : null;

                if (video) {
                    return video;
                }
            }
        }

        if (this._canvasEl) {
            const container = this._canvasEl.closest('.videocontainer');

            if (container) {
                const video = container.querySelector('video');

                if (video) {
                    return video;
                }
            }
        }

        if (this.props.participantId) {
            const pContainer = document.getElementById(`participant_${this.props.participantId}`)
                || document.getElementById(`participant_LocalScreenShare_${this.props.participantId}`)
                || document.getElementById(`participant_RemoteScreenShare_${this.props.participantId}`);

            if (pContainer) {
                const video = pContainer.querySelector('video');

                if (video) {
                    return video;
                }
            }
        }

        if (this.props.isLocal) {
            const localWrapper = document.getElementById('localVideoWrapper')
                || document.getElementById('localVideoContainer');

            if (localWrapper) {
                const video = localWrapper.querySelector('video');

                if (video) {
                    return video;
                }
            }
        }

        return null;
    }

    _updateCanvas: () => void;

    /**
     * Draws the current frame of the video element onto the canvas.
     *
     * @private
     * @returns {void}
     */
    _updateCanvas() {
        if (typeof document !== 'undefined' && document.hidden) {
            return;
        }

        if (!this._canvasEl || this._canvasEl.offsetParent === null) {
            return;
        }

        if (!this._videoElement || !this._videoElement.isConnected) {
            this._videoElement = this._getVideoElement();
        }

        const video = this._videoElement;

        // A video element with no current frame renders transparent, so whatever was last drawn here shows through
        // it. That happens every time the element is handed a new track - a screenshare that is stopped and started
        // again, a participant re-joining - and lasts until the new track delivers a frame, or indefinitely if it
        // never does. Keeping the old frame then makes the tile show a blurred copy of the previous video in place of
        // the current one, so drop it. A paused element still displays its frame, so that case keeps the background.
        if (!video || video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0 || video.ended) {
            if (this.state.hasFrame) {
                this._clearCanvas();
                this.setState({ hasFrame: false });
            }

            return;
        }

        if (video.paused) {
            if (video.autoplay || video.muted) {
                video.play().catch(() => {
                    // Ignore autoplay/play rejections
                });
            }

            return;
        }

        const { videoWidth, videoHeight } = video;
        const canvas = this._canvasEl;
        const canvasWidth = canvas.width || 320;
        const canvasHeight = canvas.height || 180;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            return;
        }

        const canvasAspect = canvasWidth / canvasHeight;
        const videoAspect = videoWidth / videoHeight;
        let drawHeight, drawWidth, offsetX, offsetY;

        if (videoAspect > canvasAspect) {
            // Video is wider than canvas: match height, crop sides
            drawHeight = canvasHeight;
            drawWidth = canvasHeight * videoAspect;
            offsetX = (canvasWidth - drawWidth) / 2;
            offsetY = 0;
        } else {
            // Video is taller than canvas: match width, crop top/bottom
            drawWidth = canvasWidth;
            drawHeight = canvasWidth / videoAspect;
            offsetX = 0;
            offsetY = (canvasHeight - drawHeight) / 2;
        }

        try {
            ctx.drawImage(video, offsetX, offsetY, drawWidth, drawHeight);

            if (!this.state.hasFrame) {
                this.setState({ hasFrame: true });
            }
        } catch {
            // Ignore transient frame draw errors
        }
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { className, mirror } = this.props;
        const { hasFrame } = this.state;
        const containerClasses = [
            'thumbnail-video-background-container',
            mirror ? 'flip-x' : '',
            hasFrame ? 'has-frame' : '',
            className || ''
        ].filter(Boolean).join(' ');

        return (
            <div className = { containerClasses }>
                <canvas
                    className = 'thumbnail-video-background'
                    height = { 180 }
                    ref = { this._setCanvasEl }
                    width = { 320 } />
            </div>
        );
    }
}

export default ThumbnailVideoBackground;
